import React, { useEffect, useState } from 'react';
import SynapticCompartmentsSection from './SynapticCompartmentsSection';
import SynapseProteomesSection from './SynapseProteomesSection';
import ChapterCards from '../components/ChapterCards';
import HowToCite from '../components/HowToCite';

const About = () => {
    useEffect(() => {
        document.title = 'About SynaptopathyDB';

        // Check if there's a hash in the URL
        if (window.location.hash) {
            // Remove the # character
            const id = window.location.hash.substring(1);

            // Find the element by id
            const element = document.getElementById(id);

            // If the element exists, scroll to it
            if (element) {
                // Small delay to ensure the page is fully rendered
                setTimeout(() => {
                    element.scrollIntoView({ behavior: 'smooth' });

                    // Update active section based on hash
                    setActiveSection(id);
                }, 100);
            }
        }
    }, []); // This will run once when the component mounts

    const [activeSection, setActiveSection] = useState('overview');
    const [copiedPersonIndex, setCopiedPersonIndex] = useState(null);

    // Update the copyToClipboard function
    const copyToClipboard = (email, index) => {
        // Create temporary textarea
        const textArea = document.createElement('textarea');
        textArea.value = email;

        // Make the textarea out of viewport
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);

        // Select and copy
        textArea.focus();
        textArea.select();

        let success = false;
        try {
            success = document.execCommand('copy');
        } catch (err) {
            console.error('Unable to copy to clipboard', err);
        }

        // Remove the textarea
        document.body.removeChild(textArea);

        if (success) {
            setCopiedPersonIndex(index);
            setTimeout(() => setCopiedPersonIndex(null), 2000);
        }
    };


    const scrollToSection = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
            setActiveSection(sectionId);

            // Update URL hash without full page reload
            window.history.pushState(null, null, `#${sectionId}`);
        }
    };

    // Track scroll position to update active section
    useEffect(() => {
        const handleScroll = () => {
            const sections = [
                'overview',
                'key-features',
                'synapse-proteomes',
                'disease-classification',
                'research-team',
                // 'references',
                'contact'
            ];

            for (const section of sections) {
                const element = document.getElementById(section);
                if (element) {
                    const rect = element.getBoundingClientRect();
                    if (rect.top <= 100 && rect.bottom >= 100) {
                        setActiveSection(section);
                        break;
                    }
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-[1220px] mx-auto flex flex-col md:flex-row gap-6 ">
                {/* Left sidebar navigation */}
                <aside className="md:w-64 flex-shrink-0">
                    <div className="sticky top-24 bg-white p-4 rounded-lg shadow">
                        <nav className="space-y-2">
                            <button
                                onClick={() => scrollToSection('about')}
                                className={`block w-full text-left px-3 py-2 rounded-md transition-colors ${activeSection === 'about'
                                    ? 'bg-blue-100 text-blue-700 font-medium'
                                    : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                            >
                                About
                            </button>
                            <button
                                onClick={() => scrollToSection('overview')}
                                className={`block w-full text-left px-3 py-2 rounded-md transition-colors ${activeSection === 'overview'
                                    ? 'bg-blue-100 text-blue-700 font-medium'
                                    : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                            >
                                Overview
                            </button>

                            <button
                                onClick={() => scrollToSection('key-features')}
                                className={`block w-full text-left px-3 py-2 rounded-md transition-colors ${activeSection === 'key-features'
                                    ? 'bg-blue-100 text-blue-700 font-medium'
                                    : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                            >
                                Key Features
                            </button>

                            <button
                                onClick={() => scrollToSection('synapse-proteomes')}
                                className={`block w-full text-left px-3 py-2 rounded-md transition-colors ${activeSection === 'synapse-compartments'
                                    ? 'bg-blue-100 text-blue-700 font-medium'
                                    : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                            >
                                Synapse Proteomes
                            </button>

                            <button
                                onClick={() => scrollToSection('disease-classification')}
                                className={`block w-full text-left px-3 py-2 rounded-md transition-colors ${activeSection === 'disease-classification'
                                    ? 'bg-blue-100 text-blue-700 font-medium'
                                    : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                            >
                                Disease Classification
                            </button>

                            <button
                                onClick={() => scrollToSection('how-to-cite')}
                                className={`block w-full text-left px-3 py-2 rounded-md transition-colors ${activeSection === 'how-to-cite'
                                    ? 'bg-blue-100 text-blue-700 font-medium'
                                    : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                            >
                                How to Cite our work
                            </button>

                            <button
                                onClick={() => scrollToSection('research-team')}
                                className={`block w-full text-left px-3 py-2 rounded-md transition-colors ${activeSection === 'research-team'
                                    ? 'bg-blue-100 text-blue-700 font-medium'
                                    : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                            >
                                Research Team
                            </button>

                            {/* <button
                                onClick={() => scrollToSection('references')}
                                className={`block w-full text-left px-3 py-2 rounded-md transition-colors ${activeSection === 'references'
                                    ? 'bg-blue-100 text-blue-700 font-medium'
                                    : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                            >
                                References
                            </button> */}

                            <button
                                onClick={() => scrollToSection('contact')}
                                className={`block w-full text-left px-3 py-2 rounded-md transition-colors ${activeSection === 'contact'
                                    ? 'bg-blue-100 text-blue-700 font-medium'
                                    : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                            >
                                Contact
                            </button>
                        </nav>
                    </div>
                </aside>

                {/* Main content */}
                <div className="flex-1">
                    <header id="about" className="mb-10">
                        <h1 className="text-3xl font-bold text-gray-900 mb-4">About SynaptopathyDB</h1>
                        <p className="text-xl text-gray-600">
                            A comprehensive resource for studying the genetic and synaptic basis of brain disorders
                        </p>
                    </header>


                    <section id="overview" className="mb-12 bg-white p-6 shadow rounded-lg">
                        <h2 className="text-2xl font-semibold text-gray-800 mb-4">Overview</h2>
                        <p className="text-gray-700 mb-4">
                            Synaptic dysfunction resulting from mutations in genes encoding synaptic proteins is as a major contributor to brain and behavioral disorders, collectively termed synaptopathies. To facilitate research into the genetic basis and clinical manifestations of synaptopathy, we have created SynaptopathyDB which integrates data from 64 mammalian synapse proteomic studies with multiple genetic and phenotypic resources. We identified a consensus set of 3,437 mammalian synapse proteins and found 1,236 human genes (36%) were associated with 347 disorders of the nervous system and 947 nervous system phenotypes.
                        </p>
                    </section>

                    <section id="key-features" className="mb-12 bg-white p-6 shadow rounded-lg">
                        <h2 className="text-2xl font-semibold text-gray-800 mb-4">Key Features</h2>

                        <div className="space-y-4">
                            <div>
                                <h3 className="text-xl font-medium text-blue-600">Consensus Synapse Proteins</h3>
                                <p className="text-gray-700 mt-1">
                                    SynaptopathyDB includes proteins reported in five or more of the 64 studies to reduce
                                    potential contaminants, resulting in a total of 3,437 synapse proteins across various
                                    synaptic compartments.
                                </p>
                            </div>

                            <div>
                                <h3 className="text-xl font-medium text-blue-600">Comprehensive Disease Mapping</h3>
                                <p className="text-gray-700 mt-1">
                                    We found that 1,236 (36%) of the synapse genes had mutations associated with one or
                                    more diseases, affecting both central and peripheral nervous systems.
                                </p>
                            </div>

                            <div>
                                <h3 className="text-xl font-medium text-blue-600">Intuitive Search Interfaces</h3>
                                <p className="text-gray-700 mt-1">
                                    The platform offers three main search options: Gene Search, Disease Search, and
                                    Paper Search, each with powerful filtering tools and detailed result panels.
                                </p>
                            </div>

                            <div>
                                <h3 className="text-xl font-medium text-blue-600">Data Visualization</h3>
                                <p className="text-gray-700 mt-1">
                                    Interactive visualizations track disease trends, discovery rates, and research
                                    patterns over time through customizable charts.
                                </p>
                            </div>

                            <div>
                                <h3 className="text-xl font-medium text-blue-600">API Access</h3>
                                <p className="text-gray-700 mt-1">
                                    Researchers can access custom datasets in CSV format through our API for further analysis.
                                </p>
                            </div>
                        </div>
                    </section>

                    <section id="synapse-proteomes" className="mb-12 bg-white p-1 shadow rounded-lg">
                        {/* Synapse Compartments Section */}
                        {/* <SynapticCompartmentsSection /> */}
                        <SynapseProteomesSection />
                    </section>

                    <section id="disease-classification" className="mb-12 bg-white p-6 shadow rounded-lg">
                        <h2 className="text-2xl font-semibold text-gray-800 mb-4">International Classification of Diseases</h2>
                        <p className="text-gray-700 mb-4">
                            Synapse gene mutations affect various body systems, with the largest impact on:
                        </p>

                        <ChapterCards />
                    </section>

                    <section className="mb-12">
                        <HowToCite />
                    </section>

                    <section id="research-team" className="mb-12 bg-white p-6 shadow rounded-lg">
                        <h2 className="text-2xl font-semibold text-gray-800 mb-4">Research Team</h2>
                        <p className="text-gray-700 mb-6">
                            SynaptopathyDB was developed by a multidisciplinary team of researchers combining expertise in bioinformatics, neuroscience, data science, and computational biology:
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Dr. Oksana Sorokina */}
                            <div className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                                <div className="flex justify-between items-start">
                                    <h3 className="text-lg font-medium text-blue-700">Dr. Oksana Sorokina</h3>
                                    <button
                                        onClick={() => copyToClipboard('Oksana.Sorokina@ed.ac.uk', 0)}
                                        className="text-blue-500 hover:text-blue-700 text-sm"
                                    >
                                        {copiedPersonIndex === 0 ? "Email Copied" : "Copy Email"}
                                    </button>
                                </div>
                                <p className="text-sm text-gray-500 mb-2">
                                    Senior Researcher, School of Informatics, University of Edinburgh
                                </p>
                                <p className="text-gray-700 text-sm">
                                    Dr. Oksana Sorokina holds a PhD in Systems Biology from the University of Edinburgh and specializes in computational systems biology. Her research focuses on the network analysis of molecular signaling and the synaptic proteome, developing innovative informatics tools and databases to support neuroscience. She has co-developed the BioNAR package and contributed to comprehensive models linking synaptic proteins to brain disorders.
                                </p>
                            </div>

                            {/* Digin Dominic */}
                            <div className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                                <div className="flex justify-between items-start">
                                    <h3 className="text-lg font-medium text-blue-700">Digin Dominic</h3>
                                    <button
                                        onClick={() => copyToClipboard('Digin.Dominic@ed.ac.uk', 1)}
                                        className="text-blue-500 hover:text-blue-700 text-sm"
                                    >
                                        {copiedPersonIndex === 1 ? "Email Copied" : "Copy Email"}
                                    </button>
                                </div>
                                <p className="text-sm text-gray-500 mb-2">
                                    Informatics Engineer, University of Edinburgh
                                </p>
                                <p className="text-gray-700 text-sm">
                                    Digin Dominic works as an Informatics Engineer within Professor Seth Grant’s neuroscience research group. He manages the group’s computational infrastructure and develops systems that ensure large-scale synapse mapping data is accessible and analyzable. His work supports biomedical data analysis by integrating imaging pipelines, computing resources, and data platforms. Digin also leads the design of automation tools and visualization systems, playing a key role in advancing research into brain function and disease.
                                </p>
                            </div>

                            {/* Dr. Àlex Bayés */}
                            <div className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                                <div className="flex justify-between items-start">
                                    <h3 className="text-lg font-medium text-blue-700">Dr. Àlex Bayés</h3>
                                    <button
                                        onClick={() => copyToClipboard('ABayesP@santpau.cat', 3)}
                                        className="text-blue-500 hover:text-blue-700 text-sm"
                                    >
                                        {copiedPersonIndex === 3 ? "Email Copied" : "Copy Email"}
                                    </button>
                                </div>
                                <p className="text-sm text-gray-500 mb-2">
                                    Principal Investigator, Institut de Recerca Sant Pau (IR SANT PAU)
                                </p>
                                <p className="text-gray-700 text-sm">
                                    Dr. Àlex Bayés is the Principal Investigator of the Molecular Physiology of the Synapse Laboratory at the Institut de Recerca Sant Pau (IR SANT PAU). He earned his PhD in Biochemistry from the Universitat Autònoma de Barcelona and completed postdoctoral research at the Wellcome Sanger Institute and University of Edinburgh. His research investigates the molecular and cellular biology of synapses, pioneering large-scale proteomic studies that link synaptic proteins to neurological diseases.
                                </p>
                            </div>

                            {/* Professor J. Douglas Armstrong */}
                            <div className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                                <div className="flex justify-between items-start">
                                    <h3 className="text-lg font-medium text-blue-700">Professor J. Douglas Armstrong</h3>
                                    <button
                                        onClick={() => copyToClipboard('Douglas.Armstrong@ed.ac.uk', 4)}
                                        className="text-blue-500 hover:text-blue-700 text-sm"
                                    >
                                        {copiedPersonIndex === 4 ? "Email Copied" : "Copy Email"}
                                    </button>
                                </div>
                                <p className="text-sm text-gray-500 mb-2">
                                    Professor of Systems Neurobiology, School of Informatics, University of Edinburgh
                                </p>
                                <p className="text-gray-700 text-sm">
                                    Professor J. Douglas Armstrong is a leading expert in systems neurobiology and the Deputy Director of the Edinburgh Centre for Bioinformatics. His research focuses on modeling large synaptic protein complexes and understanding how mutations in these networks can lead to psychiatric disorders. He has co-developed numerous neuroinformatics tools and databases, contributing significantly to the mapping of postsynaptic protein complexes.
                                </p>
                            </div>


                        </div>
                        {/* Professor Seth G.N. Grant */}
                        <div className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow mt-6">
                            <div className="flex justify-between items-start">
                                <h3 className="text-lg font-medium text-blue-700">Professor Seth G.N. Grant</h3>
                                <button
                                    onClick={() => copyToClipboard('Seth.Grant@ed.ac.uk', 5)}
                                    className="text-blue-500 hover:text-blue-700 text-sm"
                                >
                                    {copiedPersonIndex === 5 ? "Email Copied" : "Copy Email"}
                                </button>
                            </div>
                            <p className="text-sm text-gray-500 mb-2">
                                Professor of Molecular Neuroscience, University of Edinburgh
                            </p>
                            <p className="text-gray-700 text-sm">
                                Professor Seth G.N. Grant is internationally recognised for groundbreaking research into the biology of synapses—the connections between neurons that underpin all brain function. His laboratory has pioneered the use of proteomics and genetics to uncover how mutations in synaptic proteins contribute to neurological and psychiatric disorders, including schizophrenia.
                            </p>
                            <p className="text-gray-700 text-sm mt-2">

                                A major focus of Professor Grant’s work has been the discovery that synaptic proteins form multiprotein complexes critical for learning, memory, and behaviour. His research has shown that disruption of these complexes impairs brain function and can lead to disease.
                            </p>
                            <p className="text-gray-700 text-sm mt-2">

                                Professor Grant’s team produced the first brain-wide maps of synapses at single-synapse resolution, revealing the synaptome architecture – a spatially organised landscape of molecularly diverse synapses across the brain. This research has provided a roadmap for understanding how gene mutations selectively affect specific synapse types and neural circuits, with profound implications for neuroscience, psychiatry, and precision medicine.
                            </p>
                        </div>
                    </section>


                    {/* <section id="references" className="mb-12 bg-white p-6 shadow rounded-lg">
                        <h2 className="text-2xl font-semibold text-gray-800 mb-4">References</h2>

                        <ul className="space-y-4 text-gray-700">
                            <li className="pl-4 border-l-2 border-gray-300 hover:bg-gray-50 transition-colors">
                                <a href="https://doi.org/10.1038/76615" target="_blank" rel="noopener noreferrer" className="block text-blue-600 hover:underline">
                                    <span className="block">Husi, H., Ward, M. A., Choudhary, J. S., Blackstock, W. P. & Grant, S. G. (2000).</span>
                                    <span className="italic">Proteomic analysis of NMDA receptor-adhesion protein signaling complexes.</span>
                                    <span className="block">Nat Neurosci 3, 661-669.</span>
                                </a>
                            </li>

                            <li className="pl-4 border-l-2 border-gray-300 hover:bg-gray-50 transition-colors">
                                <a href="https://doi.org/10.1038/nn.2719" target="_blank" rel="noopener noreferrer" className="block text-blue-600 hover:underline">
                                    <span className="block">Bayes, A. et al. (2011).</span>
                                    <span className="italic">Characterization of the proteome, diseases and evolution of the human postsynaptic density.</span>
                                    <span className="block">Nat Neurosci 14, 19-21.</span>
                                </a>
                            </li>

                            <li className="pl-4 border-l-2 border-gray-300 hover:bg-gray-50 transition-colors">
                                <a href="https://pubmed.ncbi.nlm.nih.gov/33976238/" target="_blank" rel="noopener noreferrer" className="block text-blue-600 hover:underline">
                                    <span className="block">Sorokina, O. et al. (2021).</span>
                                    <span className="italic">A unified resource and configurable model of the synapse proteome and its role in disease.</span>
                                    <span className="block">Sci Rep 11, 9967.</span>
                                </a>
                            </li>

                            <li className="pl-4 border-l-2 border-gray-300 hover:bg-gray-50 transition-colors">
                                <a href="https://doi.org/10.1371/journal.pone.0046683" target="_blank" rel="noopener noreferrer" className="block text-blue-600 hover:underline">
                                    <span className="block">Bayes, A. et al. (2012).</span>
                                    <span className="italic">Comparative study of human and mouse postsynaptic proteomes finds high compositional conservation and abundance differences for key synaptic proteins.</span>
                                    <span className="block">PLoS One 7, e46683.</span>
                                </a>
                            </li>

                            <li className="pl-4 border-l-2 border-gray-300 hover:bg-gray-50 transition-colors">
                                <a href="https://doi.org/10.1038/ncomms14613" target="_blank" rel="noopener noreferrer" className="block text-blue-600 hover:underline">
                                    <span className="block">Bayes, A. et al. (2017).</span>
                                    <span className="italic">Evolution of complexity in the zebrafish synapse proteome.</span>
                                    <span className="block">Nat Commun 8, 14613.</span>
                                </a>
                            </li>

                            <li className="pl-4 border-l-2 border-gray-300 hover:bg-gray-50 transition-colors">
                                <a href="https://doi.org/10.1038/nn.2135" target="_blank" rel="noopener noreferrer" className="block text-blue-600 hover:underline">
                                    <span className="block">Emes, R. D. et al. (2008).</span>
                                    <span className="italic">Evolutionary expansion and anatomical specialization of synapse proteome complexity.</span>
                                    <span className="block">Nat Neurosci 11, 799-806.</span>
                                </a>
                            </li>

                            <li className="pl-4 border-l-2 border-gray-300 hover:bg-gray-50 transition-colors">
                                <a href="https://doi.org/10.1016/j.neuropharm.2021.108640" target="_blank" rel="noopener noreferrer" className="block text-blue-600 hover:underline">
                                    <span className="block">Ramos-Vicente, D., Grant, S. G. & Bayes, A. (2021).</span>
                                    <span className="italic">Metazoan evolution and diversity of glutamate receptors and their auxiliary subunits.</span>
                                    <span className="block">Neuropharmacology 195, 108640.</span>
                                </a>
                            </li>
                            <li className="pl-4 border-l-2 border-gray-300 hover:bg-gray-50 transition-colors">
                                <a href="https://doi.org/10.1093/nar/gks1114" target="_blank" rel="noopener noreferrer" className="block text-blue-600 hover:underline">
                                    <span className="block">Wu, C., Macleod, I. & Su, A.I. (2013).</span>
                                    <span className="italic">BioGPS and MyGene.info: organizing online, gene-centric information.</span>
                                    <span className="block">Nucleic Acids Res. 41(Database issue), D561-5.</span>
                                </a>
                            </li>

                            <li className="pl-4 border-l-2 border-gray-300 hover:bg-gray-50 transition-colors">
                                <a href="https://doi.org/10.1186/s13059-016-0953-9" target="_blank" rel="noopener noreferrer" className="block text-blue-600 hover:underline">
                                    <span className="block">Xin, J., Mark, A., Afrasiabi, C., Tsueng, G., Juchler, M., Gopal, N., Stupp, G.S., Putman, T.E., Ainscough, B.J., Griffith, O.L., Torkamani, A., Whetzel, P.L., Mungall, C.J., Mooney, S.D., Su, A.I. & Wu, C. (2016).</span>
                                    <span className="italic">High-performance web services for querying gene and variant annotation.</span>
                                    <span className="block">Genome Biol. 17(1), 91.</span>
                                </a>
                            </li>

                            <li className="pl-4 border-l-2 border-gray-300 hover:bg-gray-50 transition-colors">
                                <a href="https://doi.org/10.1093/bioinformatics/btac017" target="_blank" rel="noopener noreferrer" className="block text-blue-600 hover:underline">
                                    <span className="block">Lelong, S., Zhou, X., Afrasiabi, C., Qian, Z., Cano, M.A., Tsueng, G., Xin, J., Mullen, J., Yao, Y., Avila, R., Taylor, G., Su, A.I. & Wu, C. (2022).</span>
                                    <span className="italic">BioThings SDK: a toolkit for building high-performance data APIs in biomedical research.</span>
                                    <span className="block">Bioinformatics. 38(7), 2077-2079.</span>
                                </a>
                            </li>

                            <li className="pl-4 border-l-2 border-gray-300 hover:bg-gray-50 transition-colors">
                                <a href="https://www.omim.org/" target="_blank" rel="noopener noreferrer" className="block text-blue-600 hover:underline">
                                    <span className="block">Online Mendelian Inheritance in Man, OMIM. (2024).</span>
                                </a>
                            </li>
                        </ul>
                    </section> */}

                    <section id="contact" className="bg-white p-6 shadow rounded-lg">
                        <h2 className="text-2xl font-semibold text-gray-800 mb-4">Contact</h2>
                        <p className="text-gray-700 mb-4">
                            For inquiries regarding SynaptopathyDB, please contact:
                        </p>

                        <div className="bg-blue-50 p-4 rounded-lg">
                            <p className="text-gray-700">Email: contact@synaptopathyDB.org</p>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default About;